import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { AcreageReportService } from '../../core/api-services/acreage-report.service';
import { ChemicalReportService } from '../../core/api-services/chemical-report.service';
import { AcreageReportOrgDetail } from '../../core/response-models/acreage-report-org-detail.model';
import { ChemicalReportOrgDetail } from '../../core/response-models/chemical-report-org-detail.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import { Drone } from '../../core/data-models/drone.model';
import { DroneAssignment } from '../../core/data-models/drone-assignment.model';
import { AssignmentType } from '../../core/data-models/assignment-type.model';
import { LeaseStatus } from '../../core/data-models/lease-status.model';
import { SettableDroneStatus } from '../../core/data-models/settable-drone-status.model';
import { DroneService } from '../../core/data-services/drone.service';
import { OrganizationService } from '../../core/data-services/organization.service';
import { ZohoSyncService } from '../../core/api-services/zoho-sync.service';
import { DRONE_RESOURCE_TYPES } from 'src/app/shared/constants/drone-resource-options';
import { DroneResource } from 'src/app/core/data-models/drone-resource.model';
import { RemoveDashPipe } from "src/app/shared/pipes/remove-dash.pipe";
import { DroneResourceService } from 'src/app/core/data-services/drone-resource.service';
import { DroneResourceUploadModalComponent } from 'src/app/shared/components/drone-resource-upload-modal.component';
import { FileUploadService } from 'src/app/core/api-services/file-upload.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal.component';
import { isHttpsUrl, openExternal } from '../../core/utils/external-link-utils';
import { formatCalendarDate, toCalendarDateInput, toCalendarDateString } from '../../shared/utils/date.utils';
import {
  canChangeDroneStatus,
  getAssignmentTypeLabel,
  getDroneStatusLabel,
  getLeaseStatusLabel,
} from '../../shared/utils/drone-status.utils';
import { deriveLeaseStatus, isLeaseStatusFromDates, LeaseFormValues, leaseFormError } from '../../shared/utils/lease-form.utils';

declare var bootstrap: any;

@Component({
  selector: 'app-drone-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RemoveDashPipe, DroneResourceUploadModalComponent, ConfirmationModalComponent],
  providers: [RemoveDashPipe],
  templateUrl: './drone-detail.component.html',
  styleUrl: './drone-detail.component.scss'
})
export class DroneDetailComponent implements OnInit {
  drone: Drone | null = null;
  loading = true;
  errorMessage = '';
  droneId: string | null = null;
  archiving = false;
  syncing = false;
  syncSuccessMessage = '';
  private archiveModal: any;

  // Assignment state
  currentAssignment: DroneAssignment | null = null;
  assignmentHistory: DroneAssignment[] = [];
  organizations: OrganizationOption[] = [];
  loadingAssignment = false;
  loadingOrganizations = false;
  assigning = false;
  returning = false;
  revoking = false;

  // Assignment form
  selectedOrganizationId = '';
  assignmentType: AssignmentType | null = null;
  assignmentNotes = '';

  // Edit-lease form (ADN-2208). Dates are "YYYY-MM-DD" input values, '' when empty.
  savingLease = false;
  leaseErrorMessage = '';
  leaseForm: LeaseFormValues = { assignmentType: null, leaseStatus: null, leaseStart: '', leaseEnd: '', leaseTermination: '' };
  leaseNotes = '';
  returnNotes = '';
  revokeReason = '';

  // Organization search
  organizationSearchQuery = '';
  filteredOrganizations: OrganizationOption[] = [];
  showOrganizationDropdown = false;
  selectedOrganization: OrganizationOption | null = null;

  // Modals
  private assignModal: any;
  private leaseModal: any;
  private returnModal: any;
  private revokeModal: any;
  private historyModal: any;

  // Reports section
  reportsExpanded = false;
  loadingReports = false;
  reportErrorMessage = '';
  reportStartDate = '';
  reportEndDate = '';
  acreageData: AcreageReportOrgDetail | null = null;
  chemicalData: ChemicalReportOrgDetail | null = null;
  exportingAcreage = false;
  exportingChemical = false;

  // Resources section
  resourcesExpanded = false;
  loadingResources = false;
  droneResources: DroneResource[] = [];
  droneResourcesErrorMessage = '';
  droneResourcesSuccessMessage = '';
  readonly resourceTypes = ['all'].concat(DRONE_RESOURCE_TYPES);
  showResourceUploadModal = false;
  editingResource: DroneResource | null = null;
  resourceType: string = 'all'
  adminOnly: string = 'all';
  showConfirmDeleteModal: boolean = false;
  deletingResource: boolean = false
  resourceToDelete: DroneResource | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private droneService: DroneService,
    private organizationService: OrganizationService,
    private zohoSyncService: ZohoSyncService,
    private acreageReportService: AcreageReportService,
    private chemicalReportService: ChemicalReportService,
    private droneResourcesService: DroneResourceService,
    private fileUploadService: FileUploadService,
    private removeDash: RemoveDashPipe
  ) {}

  ngOnInit(): void {
    this.droneId = this.route.snapshot.paramMap.get('id');
    if (this.droneId) {
      this.loadDroneDetail();
      this.loadCurrentAssignment();
      this.loadOrganizations();
      // Resources are NOT fetched here — the section starts collapsed and its
      // header shows no count, so toggleResourcesSection owns the first load.
      this.initializeReportDateRange();
    } else {
      this.errorMessage = 'No drone ID provided';
      this.loading = false;
    }
  }

  async loadDroneDetail(): Promise<void> {
    if (!this.droneId) return;
    this.loading = true;
    this.errorMessage = '';

    try {
      this.drone = await firstValueFrom(this.droneService.getDroneById(this.droneId));
      if (!this.drone) {
        this.errorMessage = 'Failed to load drone details';
      }
    } catch (error) {
      console.error('Failed to load drone:', error);
      this.errorMessage = ApiError.fromHttpError(error).status === 404 ? 'Drone not found' : 'Failed to load drone details';
    } finally {
      this.loading = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/drones']);
  }

  editDrone(): void {
    this.router.navigate(['/drones', this.droneId, 'edit']);
  }

  confirmArchive(): void {
    const modalElement = document.getElementById('archiveModal');
    if (modalElement) {
      this.archiveModal = new bootstrap.Modal(modalElement);
      this.archiveModal.show();
    }
  }

  async archiveDrone(): Promise<void> {
    if (!this.droneId) return;

    this.archiving = true;

    try {
      await firstValueFrom(this.droneService.archiveDrone(this.droneId));

      if (this.archiveModal) {
        this.archiveModal.hide();
      }
      this.router.navigate(['/drones']);
    } catch (error: any) {
      console.error('Failed to archive drone:', error);
      this.errorMessage = 'Failed to archive drone. Please try again.';
      if (this.archiveModal) {
        this.archiveModal.hide();
      }
    } finally {
      this.archiving = false;
    }
  }

  /** Whether the status button for `target` is enabled (a leased drone can only go into maintenance). */
  canChangeStatus(target: SettableDroneStatus): boolean {
    return canChangeDroneStatus(this.drone?.status, target);
  }

  /** True while the hub would reject a lease edit: it needs the drone assigned or leased. */
  get leaseEditBlocked(): boolean {
    return this.drone?.status !== 'assigned' && this.drone?.status !== 'leased';
  }

  async updateStatus(newStatus: SettableDroneStatus): Promise<void> {
    if (!this.droneId) return;

    try {
      // Back to service from maintenance also restores a saved lease (see DroneService.returnToService)
      const backToService = newStatus === 'assigned' && this.drone?.status === 'maintenance';
      const updated = backToService
        ? await this.droneService.returnToService(this.droneId)
        : await firstValueFrom(this.droneService.updateStatus(this.droneId, newStatus));
      if (updated) {
        this.drone = updated;
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to update drone status';
    }
  }

  getStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'available': return 'bg-success';
      case 'assigned': return 'bg-primary';
      case 'leased': return 'bg-primary';
      case 'maintenance': return 'bg-warning';
      case 'retired': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  /** The drone's status, saying whether an assigned drone is a demo or awaiting its lease. */
  getStatusLabel(status: Drone['status']): string {
    return status ? getDroneStatusLabel(status, this.currentAssignment?.assignmentType) : '—';
  }

  getAssignmentTypeLabel(type: AssignmentType | null | undefined): string {
    return getAssignmentTypeLabel(type);
  }

  getLeaseStatusLabel(status: LeaseStatus | null | undefined): string {
    return getLeaseStatusLabel(status);
  }

  /** A lease date (a Postgres `date`), shown without timezone drift. */
  formatLeaseDate(value: string | null | undefined): string {
    return formatCalendarDate(value, undefined, '—');
  }

  formatDate(value: Date | string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(value: Date | string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getZohoSyncStatusClass(): string {
    switch (this.drone?.zohoFsmSyncStatus) {
      case 'synced': return 'bg-success';
      case 'pending': return 'bg-warning';
      case 'error': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getZohoSyncStatusLabel(): string {
    switch (this.drone?.zohoFsmSyncStatus) {
      case 'synced': return 'Synced';
      case 'pending': return 'Pending';
      case 'error': return 'Error';
      default: return 'Not Synced';
    }
  }

  async syncToZoho(): Promise<void> {
    if (!this.droneId || this.syncing) return;

    this.syncing = true;
    this.errorMessage = '';
    this.syncSuccessMessage = '';

    try {
      const result = await firstValueFrom(this.zohoSyncService.syncDrone(this.droneId));

      if (result.success) {
        this.syncSuccessMessage = result.operation === 'create'
          ? 'Drone created in Zoho FSM'
          : 'Drone updated in Zoho FSM';
        // Reload drone to get updated sync status
        await this.loadDroneDetail();
      } else {
        this.errorMessage = result.error || 'Failed to sync drone to Zoho';
      }
    } catch (error) {
      console.error('Failed to sync to Zoho:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to sync drone to Zoho';
    } finally {
      this.syncing = false;
    }
  }

  // ============================================
  // Assignment Methods
  // ============================================

  async loadCurrentAssignment(): Promise<void> {
    if (!this.droneId) return;

    this.loadingAssignment = true;
    try {
      const { assignment } = await firstValueFrom(this.droneService.getCurrentAssignment(this.droneId));
      this.currentAssignment = assignment;

      // If reports section was expanded before assignment loaded, trigger report load now
      if (this.reportsExpanded && !this.acreageData && !this.chemicalData) {
        this.loadReportData();
      }
    } catch (error) {
      console.error('Failed to load current assignment:', error);
    } finally {
      this.loadingAssignment = false;
    }
  }

  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;
    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
      this.filteredOrganizations = [...this.organizations];
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      this.loadingOrganizations = false;
    }
  }

  async loadAssignmentHistory(): Promise<void> {
    if (!this.droneId) return;

    try {
      this.assignmentHistory = await firstValueFrom(this.droneService.getDroneAssignmentHistory(this.droneId));
    } catch (error) {
      console.error('Failed to load assignment history:', error);
    }
  }

  // Modal controls
  openAssignModal(): void {
    this.selectedOrganizationId = '';
    this.selectedOrganization = null;
    this.organizationSearchQuery = '';
    this.filteredOrganizations = [...this.organizations];
    this.showOrganizationDropdown = false;
    this.assignmentType = null;
    this.assignmentNotes = '';
    const modalElement = document.getElementById('assignModal');
    if (modalElement) {
      this.assignModal = new bootstrap.Modal(modalElement);
      this.assignModal.show();
    }
  }

  // Organization search methods
  onOrganizationSearchInput(): void {
    const query = this.organizationSearchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredOrganizations = [...this.organizations];
    } else {
      this.filteredOrganizations = this.organizations.filter(org =>
        org.name.toLowerCase().includes(query)
      );
    }
    this.showOrganizationDropdown = true;
  }

  onOrganizationSearchFocus(): void {
    this.showOrganizationDropdown = true;
  }

  onOrganizationSearchBlur(): void {
    // Delay to allow click on dropdown item
    setTimeout(() => {
      this.showOrganizationDropdown = false;
    }, 200);
  }

  selectOrganization(org: OrganizationOption): void {
    this.selectedOrganization = org;
    this.selectedOrganizationId = org.id;
    this.organizationSearchQuery = org.name;
    this.showOrganizationDropdown = false;
  }

  clearOrganizationSelection(): void {
    this.selectedOrganization = null;
    this.selectedOrganizationId = '';
    this.organizationSearchQuery = '';
    this.filteredOrganizations = [...this.organizations];
  }

  openReturnModal(): void {
    this.returnNotes = '';
    const modalElement = document.getElementById('returnModal');
    if (modalElement) {
      this.returnModal = new bootstrap.Modal(modalElement);
      this.returnModal.show();
    }
  }

  openRevokeModal(): void {
    this.revokeReason = '';
    const modalElement = document.getElementById('revokeModal');
    if (modalElement) {
      this.revokeModal = new bootstrap.Modal(modalElement);
      this.revokeModal.show();
    }
  }

  async openHistoryModal(): Promise<void> {
    await this.loadAssignmentHistory();
    const modalElement = document.getElementById('historyModal');
    if (modalElement) {
      this.historyModal = new bootstrap.Modal(modalElement);
      this.historyModal.show();
    }
  }

  // Assignment actions
  async assignDrone(): Promise<void> {
    if (!this.droneId || !this.selectedOrganizationId || !this.assignmentType || this.assigning) return;

    this.assigning = true;
    this.errorMessage = '';

    try {
      this.currentAssignment = await firstValueFrom(
        this.droneService.assignDrone(
          this.droneId,
          this.selectedOrganizationId,
          this.assignmentType,
          undefined,
          this.assignmentNotes || undefined,
        )
      );
      this.assignModal?.hide();
      // Reload drone to get updated status
      await this.loadDroneDetail();
    } catch (error) {
      console.error('Failed to assign drone:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to assign drone';
    } finally {
      this.assigning = false;
    }
  }

  // Lease actions (ADN-2208)

  /** Fills the edit-lease form from the active assignment and opens it. */
  openEditLeaseModal(): void {
    const assignment = this.currentAssignment;
    this.leaseErrorMessage = '';
    this.leaseForm = {
      assignmentType: assignment?.assignmentType ?? null,
      leaseStatus: assignment?.leaseStatus ?? null,
      leaseStart: toCalendarDateInput(assignment?.leaseStart),
      leaseEnd: toCalendarDateInput(assignment?.leaseEnd),
      leaseTermination: toCalendarDateInput(assignment?.leaseTermination),
    };
    this.leaseNotes = assignment?.leaseNotes ?? '';
    this.onLeaseDatesChange();
    const modalElement = document.getElementById('editLeaseModal');
    if (modalElement) {
      this.leaseModal = new bootstrap.Modal(modalElement);
      this.leaseModal.show();
    }
  }

  /** Re-derives the lease status after a date changes (see `deriveLeaseStatus`). */
  onLeaseDatesChange(): void {
    if (this.leaseForm.assignmentType !== 'leased') return;
    const saved = this.currentAssignment?.leaseStatus ?? null;
    const today = toCalendarDateString(new Date());
    this.leaseForm.leaseStatus = deriveLeaseStatus(this.leaseForm, this.leaseForm.leaseStatus, saved, today);
  }

  /** True when the dates decide the lease status, so the status picker is locked. */
  get leaseStatusFromDates(): boolean {
    return isLeaseStatusFromDates(this.leaseForm.leaseStatus);
  }

  async saveLease(): Promise<void> {
    const organizationId = this.currentAssignment?.organizationId;
    if (!this.droneId || !organizationId || this.savingLease) return;

    this.leaseErrorMessage = leaseFormError(this.leaseForm) ?? '';
    if (this.leaseErrorMessage || !this.leaseForm.assignmentType) return;

    this.savingLease = true;
    try {
      const updated = await firstValueFrom(this.droneService.updateLease(this.droneId, {
        organizationId,
        ...this.leaseForm,
        assignmentType: this.leaseForm.assignmentType,
        leaseNotes: this.leaseNotes,
      }));
      this.currentAssignment = updated ?? this.currentAssignment;
      this.leaseModal?.hide();
      await this.loadDroneDetail();
    } catch (error) {
      console.error('Failed to update drone lease:', error);
      this.leaseErrorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to update drone lease';
    } finally {
      this.savingLease = false;
    }
  }

  async returnDrone(): Promise<void> {
    if (!this.droneId || this.returning) return;

    this.returning = true;
    this.errorMessage = '';

    try {
      await firstValueFrom(this.droneService.returnDrone(this.droneId, this.returnNotes || undefined));
      this.currentAssignment = null;
      this.returnModal?.hide();
      // Reload drone to get updated status
      await this.loadDroneDetail();
    } catch (error) {
      console.error('Failed to return drone:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to return drone';
    } finally {
      this.returning = false;
    }
  }

  async revokeDrone(): Promise<void> {
    if (!this.droneId || !this.revokeReason.trim() || this.revoking) return;

    this.revoking = true;
    this.errorMessage = '';

    try {
      await firstValueFrom(this.droneService.revokeAssignment(this.droneId, this.revokeReason.trim()));
      this.currentAssignment = null;
      this.revokeModal?.hide();
      // Reload drone to get updated status
      await this.loadDroneDetail();
    } catch (error) {
      console.error('Failed to revoke assignment:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to revoke assignment';
    } finally {
      this.revoking = false;
    }
  }

  getAssignmentStatusClass(status: string | null | undefined): string {
    switch (status) {
      case 'active': return 'bg-success';
      case 'returned': return 'bg-secondary';
      case 'revoked': return 'bg-danger';
      case 'lost': return 'bg-dark';
      case 'damaged': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getAssignmentStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'active': return 'Active';
      case 'returned': return 'Returned';
      case 'revoked': return 'Revoked';
      case 'lost': return 'Lost';
      case 'damaged': return 'Damaged';
      default: return status ?? '—';
    }
  }

  getOrganizationName(organizationId: string | null | undefined): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || 'Unknown Organization';
  }

  // ============================================
  // Reports Section Methods
  // ============================================

  /**
   * Initialize date range for reports to current month (first and last day)
   */
  private initializeReportDateRange(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.reportStartDate = this.formatDateForInput(firstDay);
    this.reportEndDate = this.formatDateForInput(lastDay);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Toggle reports section expansion
   */
  toggleReportsSection(): void {
    this.reportsExpanded = !this.reportsExpanded;

    // Load reports data when expanding for the first time
    if (this.reportsExpanded && !this.acreageData && !this.chemicalData) {
      this.loadReportData();
    }
  }

  toggleResourcesSection(): void {
    this.resourcesExpanded = !this.resourcesExpanded;

    if (this.resourcesExpanded) {
      this.loadDroneResources();
    }
  }

  /**
   * Handle report filter change
   */
  onReportFilterChange(): void {
    if (this.reportsExpanded) {
      this.loadReportData();
    }
  }

  /**
   * Load both acreage and chemical report data scoped to this drone
   */
  async loadReportData(): Promise<void> {
    const organizationId = this.currentAssignment?.organizationId;
    if (!this.droneId || !this.reportStartDate || !this.reportEndDate || !organizationId) {
      // Reports require an active assignment to have an organization context
      return;
    }

    this.loadingReports = true;
    this.reportErrorMessage = '';

    try {
      // Load both reports in parallel, scoped to this drone
      const [acreage, chemical] = await Promise.all([
        this.acreageReportService.getOrganizationDetail(
          organizationId,
          this.reportStartDate,
          this.reportEndDate,
          this.droneId
        ),
        this.chemicalReportService.getOrganizationDetail(
          organizationId,
          this.reportStartDate,
          this.reportEndDate,
          undefined,
          this.droneId
        )
      ]);

      this.acreageData = acreage;
      this.chemicalData = chemical;
    } catch (error: any) {
      console.error('Failed to load report data:', error);
      this.reportErrorMessage = 'Failed to load report data. Please try again.';
    } finally {
      this.loadingReports = false;
    }
  }

  async loadDroneResources(): Promise<void> {
    if (!this.droneId) {
      return;
    }

    this.loadingResources = true;
    this.droneResourcesErrorMessage = '';

    const resourceTypeClean = this.resourceType === 'all' ? '' : this.resourceType;
    const adminOnlyClean = this.adminOnly === 'all' ? '' : this.adminOnly;

    try {
      this.droneResources = await this.droneResourcesService.getResourcesByDroneId(
        this.droneId,
        resourceTypeClean,
        adminOnlyClean,
      );
    } catch (error: any) {
      console.error('Failed to load any drone resources: ', error);
      this.droneResourcesErrorMessage = 'Failed to load drone resources. Please try again';
    } finally {
      this.loadingResources = false;
    }
  }

  onResourceAdded(resource: DroneResource): void {
    // Reset the filters so the file they just added is guaranteed visible,
    // then refetch — the server decides what matches, not a local prepend.
    this.resourceType = 'all';
    this.adminOnly = 'all';
    this.closeResourceUploadModal();
    this.loadDroneResources();
    this.droneResourcesSuccessMessage = `Attached ${this.removeDash.transform(resource.resourceType ?? 'other')} document "${resource.fileName}" to ${this.drone?.name}`;

  }

  onResourceUpdated(resource: DroneResource): void {
    // Filters stay put here: an edit that no longer matches SHOULD drop out.
    this.closeResourceUploadModal();
    this.loadDroneResources();
    this.droneResourcesSuccessMessage = `Edited ${this.removeDash.transform(resource.resourceType ?? 'other')} document "${resource.fileName}" to ${this.drone?.name}`;
  }

  async onResourceDeleteConfirm(): Promise<void> {
    if (!this.drone || !this.resourceToDelete) {
      console.error('Drone or resourceToDelete is null');
      return;
    }

    // Captured before the reset below — the success copy needs it.
    const fileName = this.resourceToDelete.fileName;
    this.deletingResource = true;

    try {
      await this.droneResourcesService.deleteResource(this.drone.id, this.resourceToDelete.id);
      this.droneResourcesSuccessMessage = `Removed "${fileName}" from ${this.drone.name}`;
      this.resourceToDelete = null;
    } catch (error: any) {
      console.error('Unable to delete resource', error);
      this.droneResourcesErrorMessage = error?.message || 'Could not delete this resource.';
    } finally {
      this.deletingResource = false;
      this.showConfirmDeleteModal = false;
      this.loadDroneResources();
    }
  }

  /**
   * Export acreage report to PDF
   */
  async exportAcreagePdf(): Promise<void> {
    const organizationId = this.currentAssignment?.organizationId;
    if (!this.acreageData || this.exportingAcreage || !organizationId) return;

    this.exportingAcreage = true;
    this.reportErrorMessage = '';

    try {
      const filters = {
        startDate: this.reportStartDate,
        endDate: this.reportEndDate,
        organizationId,
        droneId: this.droneId || undefined
      };

      const result = await this.acreageReportService.exportPdf(
        'organization-detail',
        filters,
        this.acreageData
      );

      if (result && result.downloadUrl) {
        openExternal(result.downloadUrl);
      }
    } catch (error: any) {
      console.error('Failed to export acreage PDF:', error);
      this.reportErrorMessage = 'Failed to export acreage PDF';
    } finally {
      this.exportingAcreage = false;
    }
  }

  /**
   * Export chemical report to PDF
   */
  async exportChemicalPdf(): Promise<void> {
    const organizationId = this.currentAssignment?.organizationId;
    if (!this.chemicalData || this.exportingChemical || !organizationId) return;

    this.exportingChemical = true;
    this.reportErrorMessage = '';

    try {
      const filters = {
        startDate: this.reportStartDate,
        endDate: this.reportEndDate,
        organizationId,
        droneId: this.droneId || undefined
      };

      const result = await this.chemicalReportService.exportPdf(
        'organization-detail',
        filters,
        this.chemicalData
      );

      if (result && result.downloadUrl) {
        openExternal(result.downloadUrl);
      }
    } catch (error: any) {
      console.error('Failed to export chemical PDF:', error);
      this.reportErrorMessage = 'Failed to export chemical PDF';
    } finally {
      this.exportingChemical = false;
    }
  }

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  openResourceUploadModal(resource?: DroneResource): void {
    // Null on the create path, otherwise the next "Attach Resource" reopens
    // in edit mode against the last-edited row.
    this.editingResource = resource ?? null;
    this.showResourceUploadModal = true;
  }

  openConfirmDeleteModal(resource: DroneResource): void {
    this.resourceToDelete = resource;
    this.showConfirmDeleteModal = true;
  }

  closeResourceUploadModal(): void {
    this.showResourceUploadModal = false;
    this.editingResource = null;
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined) return '';
    return this.fileUploadService.formatFileSize(bytes);
  }

  async getDroneResourceFile(resource: DroneResource) {
    if (this.drone?.id) {
      // Opened before the await so it stays inside the user-gesture window —
      // a popup blocker would eat a window.open that happens after it.
      const tab = window.open('', '_blank');
      // Sever the back-reference so the opened page can't navigate this one.
      if (tab) tab.opener = null;

      try {
        const signedUrl = await this.droneResourcesService.getResourceDownloadUrl(this.drone.id, resource.id);
        if (!isHttpsUrl(signedUrl)) {
          throw new Error('Refused a non-https resource URL');
        }
        if (tab) {
          tab.location.href = signedUrl;
        } else {
          window.location.href = signedUrl;
        }
      } catch(error) {
        tab?.close();
        this.errorMessage = 'Could not get resource file'
      }
    } else {
      console.error('Drone ID is undefined');
    }
  }
}

